import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { TspCard } from "../../../components/primitives";
import TspPlacePicker from "../../../components/common/TspPlacePicker";
import TspApiService from "../../../api/tspApiService";
import { validatePlaceDate } from "./journeyBuilderModel";

// "Add Journey Place": collapsed behind an "Add Place" button by default.
// When opened: the journey's Team Leader (`leader`, a node) at the top,
// then State -> District -> Taluka / Town / Municipality with Latitude & Longitude
// (auto-fetched or manually entered) and Visit Date, then Add Place. It sits just
// below the places already added. `notice` (a node) is shown first — e.g. an
// account without a temple. `showPicker={false}` keeps only the leader (a
// journey whose route can no longer change). `onAdd(place, date)` returns { ok } or { ok: false, message } (or a
// promise of it, when the place is saved straight away on Edit Journey).
// `coordinatesReadOnly` (Update Journey): Latitude / Longitude only show the
// auto-fetched point and cannot be typed in.
export default function JourneyPlaceAdder({
  title,
  states,
  onAdd,
  error,
  disabled,
  preset = null,
  placeCount = 0,
  leader = null,
  notice = null,
  showPicker = true,
  defaultOpen = false,
  coordinatesReadOnly = false,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen || Boolean(preset) || placeCount === 0);
  const [date, setDate] = useState("");
  const [dateError, setDateError] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [coordError, setCoordError] = useState("");
  const [locating, setLocating] = useState(false);
  const adderRef = useRef(null);

  useEffect(() => {
    if (isOpen && placeCount > 0 && adderRef.current) {
      const timer = setTimeout(() => {
        if (typeof adderRef.current?.scrollIntoView === "function") {
          adderRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, placeCount]);

  const presetKey = preset ? preset.key : null;
  const presetDate = preset ? preset.plannedDate : "";
  const presetLat = preset?.latitude != null ? String(preset.latitude) : "";
  const presetLng = preset?.longitude != null ? String(preset.longitude) : "";

  useEffect(() => {
    if (preset) setIsOpen(true);
    if (presetDate) setDate(presetDate);
    if (presetLat) setLatitude(presetLat);
    if (presetLng) setLongitude(presetLng);
    if (presetLat || presetLng) setCoordError("");
    // Applied once per preset key, like the picker's own fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetKey]);

  const handleSelectionChange = useCallback((sel) => {
    if (sel.latitude != null && sel.longitude != null) {
      setLatitude(String(sel.latitude));
      setLongitude(String(sel.longitude));
      setCoordError("");
      return;
    }
    if (typeof TspApiService?.locateMapPlace !== "function") return;
    if (sel.cityId && sel.cityId !== "other" && sel.cityId !== "__other__") {
      setLocating(true);
      TspApiService.locateMapPlace({ type: "city", id: sel.cityId })
        .then((res) => {
          if (res?.data?.latitude != null && res?.data?.longitude != null) {
            setLatitude(String(res.data.latitude));
            setLongitude(String(res.data.longitude));
            setCoordError("");
          }
        })
        .catch(() => {})
        .finally(() => setLocating(false));
    } else if (sel.districtId && (sel.isOther || !sel.cityId)) {
      setLocating(true);
      TspApiService.locateMapPlace({ type: "district", id: sel.districtId })
        .then((res) => {
          if (res?.data?.latitude != null && res?.data?.longitude != null) {
            setLatitude(String(res.data.latitude));
            setLongitude(String(res.data.longitude));
            setCoordError("");
          }
        })
        .catch(() => {})
        .finally(() => setLocating(false));
    }
  }, []);

  const add = (place) => {
    const problem = validatePlaceDate(date);
    if (problem) {
      setDateError(problem);
      document.getElementById("tsp-journey-place-date")?.focus();
      return { ok: false, message: problem };
    }
    setDateError("");

    const latStr = String(latitude ?? "").trim();
    const lngStr = String(longitude ?? "").trim();
    let coords = {};

    if (latStr || lngStr) {
      if (!latStr || !lngStr) {
        const msg = "Both latitude and longitude are required when entering coordinates.";
        setCoordError(msg);
        return { ok: false, message: msg };
      }
      const latNum = Number(latStr);
      const lngNum = Number(lngStr);
      if (Number.isNaN(latNum) || latNum < -90 || latNum > 90) {
        const msg = "Latitude must be a valid number between -90 and 90.";
        setCoordError(msg);
        return { ok: false, message: msg };
      }
      if (Number.isNaN(lngNum) || lngNum < -180 || lngNum > 180) {
        const msg = "Longitude must be a valid number between -180 and 180.";
        setCoordError(msg);
        return { ok: false, message: msg };
      }
      coords = { latitude: latNum, longitude: lngNum };
    }
    setCoordError("");

    const result = onAdd({ ...place, ...coords }, date);
    const settle = (outcome) => {
      if (outcome && outcome.ok !== false) {
        setLatitude("");
        setLongitude("");
        setCoordError("");
        setDate("");
        setDateError("");
        setIsOpen(false);
      }
    };
    if (result && typeof result.then === "function") result.then(settle);
    else settle(result);
    return result;
  };

  if (!showPicker) {
    return leader ? (
      <TspCard title="Team Leader" className="tsp-title-18 tsp-jb-adder">
        {notice}
        {leader}
      </TspCard>
    ) : null;
  }

  if (!isOpen) {
    return (
      <div className="tsp-jb-adder-trigger">
        <button
          type="button"
          id="tsp-open-add-place-btn"
          className="tsp-btn tsp-btn-red tsp-place-add-trigger"
          onClick={() => setIsOpen(true)}
          disabled={disabled}
        >
          <Plus size={16} aria-hidden="true" style={{ marginRight: 6 }} />
          <span>Add Place</span>
        </button>
      </div>
    );
  }

  return (
    <TspCard
      ref={adderRef}
      title={title || (!showPicker ? "Team Leader" : placeCount ? "Add Another Place" : "Add Journey Place")}
      subtitle={showPicker ? "Choose the state, district and taluka / town / municipality, and the date the team will be there." : undefined}
      action={
        placeCount > 0 ? (
          <button
            type="button"
            className="tsp-btn tsp-btn-outline-primary tsp-btn-sm"
            onClick={() => {
              setIsOpen(false);
              setDateError("");
              setCoordError("");
            }}
          >
            Cancel
          </button>
        ) : null
      }
      className="tsp-title-18 tsp-jb-adder tsp-jb-adder-wrapper"
    >
      {notice}
      {leader}
      <TspPlacePicker
        idPrefix="tsp-journey-route"
        states={states}
        onAdd={add}
        addLabel="Add Place"
        disabled={disabled}
        error={error}
        preset={preset}
        onSelectionChange={handleSelectionChange}
        extraFields={
          <>
            <div className={`tsp-form-field tsp-jb-coord-field${coordError ? " has-error" : ""}`}>
              <label htmlFor="tsp-journey-place-lat">
                Latitude {locating && <span className="tsp-muted-text">(fetching…)</span>}
              </label>
              <input
                id="tsp-journey-place-lat"
                type="text"
                inputMode="decimal"
                className="tsp-field-control"
                placeholder="e.g. 19.2960"
                value={latitude}
                disabled={disabled}
                readOnly={coordinatesReadOnly}
                aria-invalid={coordError ? "true" : undefined}
                onChange={(event) => {
                  setLatitude(event.target.value);
                  setCoordError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.preventDefault();
                }}
              />
            </div>
            <div className={`tsp-form-field tsp-jb-coord-field${coordError ? " has-error" : ""}`}>
              <label htmlFor="tsp-journey-place-lng">
                Longitude {locating && <span className="tsp-muted-text">(fetching…)</span>}
              </label>
              <input
                id="tsp-journey-place-lng"
                type="text"
                inputMode="decimal"
                className="tsp-field-control"
                placeholder="e.g. 73.0580"
                value={longitude}
                disabled={disabled}
                readOnly={coordinatesReadOnly}
                aria-invalid={coordError ? "true" : undefined}
                onChange={(event) => {
                  setLongitude(event.target.value);
                  setCoordError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.preventDefault();
                }}
              />
            </div>
            <div className={`tsp-form-field tsp-jb-date-field${dateError ? " has-error" : ""}`}>
              <label htmlFor="tsp-journey-place-date">
                Visit Date
                <span className="tsp-required" aria-hidden="true">
                  {" "}
                  *
                </span>
              </label>
              <input
                id="tsp-journey-place-date"
                type="date"
                className="tsp-field-control"
                value={date}
                disabled={disabled}
                aria-required="true"
                aria-invalid={dateError ? "true" : undefined}
                onChange={(event) => {
                  setDate(event.target.value);
                  setDateError("");
                }}
                onKeyDown={(event) => {
                  // Enter here must not submit the whole journey.
                  if (event.key === "Enter") event.preventDefault();
                }}
              />
            </div>
          </>
        }
      />
      {coordError && (
        <p className="tsp-form-error tsp-place-message" role="alert" style={{ marginTop: 8 }}>
          {coordError}
        </p>
      )}
    </TspCard>
  );
}
